import { useEffect } from "react";
import { useQueryClient } from "react-query";
import type { Channel } from "laravel-echo";
import { getEcho } from "../realtime/echo";
import { playNotificationSound } from "../helpers/playNotificationSound";

export interface ChatMessagePayload {
    id: number;
    chat_session_id: number;
    customer_id?: number;
    body: string;
    message: string;
    media_url: string | null;
    media_type: string | null;
    is_received: boolean;
    is_bot: boolean;
    created_at: string;
    user_id?: number;
    bot_name?: string;
}

// ---------------------------------------------------------------------------
// Internal event bus: the global hook is the ONLY Pusher listener for
// ".chat.message". Page-level hooks (useLiveChatEcho) subscribe here instead
// of binding directly to the Pusher channel, which avoids the
// stopListening-kills-all-callbacks problem.
// ---------------------------------------------------------------------------
type MessageCallback = (payload: ChatMessagePayload) => void;
const _messageListeners = new Set<MessageCallback>();

export function onChatMessage(cb: MessageCallback): () => void {
    _messageListeners.add(cb);
    return () => {
        _messageListeners.delete(cb);
    };
}

// ---------------------------------------------------------------------------

interface UseGlobalMessagingNotificationsOptions {
    facilityId: number | null | undefined;
    enabled?: boolean;
}

export function useGlobalMessagingNotifications({
    facilityId,
    enabled = true,
}: UseGlobalMessagingNotificationsOptions): void {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!enabled || !facilityId) return;

        let cancelled = false;
        let channel: Channel | null = null;

        try {
            const echo = getEcho();
            channel = echo.private(`facility.${facilityId}`);

            channel.listen(".chat.message", (payload: ChatMessagePayload) => {
                if (cancelled) return;

                if (payload.is_received && !payload.is_bot) {
                    playNotificationSound();
                }

                queryClient.invalidateQueries({
                    predicate: (q) => {
                        const k = q.queryKey[0];
                        return typeof k === "string" && k.startsWith("/unread-conversations-count");
                    },
                });

                queryClient.invalidateQueries({
                    predicate: (q) => {
                        const k = q.queryKey[0];
                        return typeof k === "string" && k.startsWith("/customers-messaging");
                    },
                });

                // Dispatch to page-level subscribers (e.g. useLiveChatEcho)
                _messageListeners.forEach((cb) => cb(payload));
            });
        } catch (e) {
            console.warn("[useGlobalMessagingNotifications] Echo subscription failed:", e);
        }

        return () => {
            cancelled = true;
            if (channel) {
                try {
                    channel.stopListening(".chat.message");
                } catch {
                    /* ignore */
                }
            }
        };
    }, [facilityId, enabled, queryClient]);
}
